import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Box, IconButton, type IconButtonProps, type Theme, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import colors from '../theme-provider/colors';

interface RecommendationCommentsButtonProps extends Omit<IconButtonProps, 'onClick'> {
    recommendationId: number;
    commentsStatus?: 'no_comments' | 'has_unread_comments' | 'has_comments';
    onOpenDrawer: (event: React.MouseEvent<HTMLButtonElement>) => void;
    enabled?: boolean;
    variant?: 'drawer' | 'list';
}

// Custom icon component matching Figma design: red chat bubble with red dot indicator
const UnreadChatIcon = ({ icon }: { icon: React.ReactNode }) => {
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
            }}
        >
            {/* Chat bubble */}
            {icon}
            {/* Red dot indicator */}
            <Box
                sx={{
                    outline: '2px solid white',
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: (theme: Theme) => theme.palette.secondary.main,
                    zIndex: 2,
                }}
            />
        </Box>
    );
};

const RecommendationCommentsButton = ({
    commentsStatus,
    onOpenDrawer,
    enabled = true,
    variant = 'drawer',
    ...iconButtonProps
}: RecommendationCommentsButtonProps) => {
    const hasUnreadComments = commentsStatus === 'has_unread_comments';
    const noComments = commentsStatus === 'no_comments';

    const icon = useMemo(() => {
        if (variant === 'list') {
            return <ChatBubbleOutlineIcon />;
        }

        return <ChatBubbleIcon />;
    }, [variant]);

    const color = useMemo(() => {
        const muiColors = colors.themes[settings.app.theme].mui;
        const secondary = muiColors.secondary.secondary;
        const primary = muiColors.primary.primary;
        const primaryInactive = muiColors.primary.colors.inactive;
        const primaryDisabled = muiColors.primary.colors.disabled;

        if (variant === 'list') {
            if (hasUnreadComments) {
                return secondary;
            }
            if (noComments) {
                return primaryDisabled;
            }

            return primaryInactive;
        }

        return hasUnreadComments ? secondary : primary;
    }, [variant, hasUnreadComments, noComments]);

    return (
        <Tooltip title={noComments ? _('No hay comentarios') : _('Ver comentarios')} arrow>
            <IconButton
                onClick={onOpenDrawer}
                size='small'
                aria-label={_('Comentarios')}
                sx={{
                    ...iconButtonProps.sx,
                    '& svg': {
                        fill: color,
                    },
                }}
                {...iconButtonProps}
            >
                {hasUnreadComments ? <UnreadChatIcon icon={icon} /> : icon}
            </IconButton>
        </Tooltip>
    );
};

export default RecommendationCommentsButton;
